import { useMemo, useState } from 'react';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { CommonFormControl } from '@/components/Common/CommonFormControl';
import { CommonNumberInput } from '@/components/Common/CommonNumberInput';
import { formatPrice } from '@/utils/helper';
import {
  buildMarketComparisonMetrics,
  DEFAULT_PROFITABILITY_ASSUMPTIONS,
  getInvestorProgram,
  getProfitabilityMetrics,
  type ProfitabilityAssumptions,
} from '@/utils/profitability';
import { Product, ProductEsitmation } from '@/types/ProductType';

type ProductDetailProfitabilityProps = {
  estimation: ProductEsitmation;
  product: Product;
};

export const ProductDetailProfitability = ({
  estimation,
  product,
}: ProductDetailProfitabilityProps) => {
  const theme = useTheme();
  const program = getInvestorProgram(
    (product?.savedLoanType as string) || product?.user?.loanProgram
  );
  const [assumptions, setAssumptions] = useState<ProfitabilityAssumptions>(
    DEFAULT_PROFITABILITY_ASSUMPTIONS
  );
  const [orderAscending, setOrderAscending] = useState(false);

  if (!program || !estimation) return null;

  const metrics = getProfitabilityMetrics(product, estimation, assumptions);
  const marketMetrics = buildMarketComparisonMetrics(
    product,
    estimation,
    assumptions
  );
  const sortedMarketMetrics = useMemo(() => {
    const sortable = [...marketMetrics];
    return sortable.sort((a, b) =>
      orderAscending ? a.value - b.value : b.value - a.value
    );
  }, [marketMetrics, orderAscending]);

  const formatPercent = (value?: number) =>
    value ? `${(value * 100).toFixed(2)}%` : 'N/A';

  const isFixAndFlip = program === USER_LOAN_PROGRAM.FIX_AND_FLIP;
  const isDscrOrCommercial =
    program === USER_LOAN_PROGRAM.DSCR ||
    program === USER_LOAN_PROGRAM.COMMERCIAL;
  const isBridge = program === USER_LOAN_PROGRAM.BRIDGE;

  return (
    <Box>
      <Card
        sx={{
          background: alpha(theme.palette.primary.main, 0.03),
          border: `1px solid ${theme.palette.greyShades.border1}`,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            gutterBottom
            sx={{ color: theme.palette.text.primary }}
          >
            Profitability Assumptions
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {isDscrOrCommercial && (
              <Grid item xs={12} md={6}>
                <CommonFormControl
                  label="Vacancy Rate (%):"
                  inLine
                  labelWidth={170}
                >
                  <CommonNumberInput
                    name="vacancyRatePercent"
                    size="small"
                    value={assumptions.vacancyRatePercent}
                    onChange={(e) =>
                      setAssumptions((prev) => ({
                        ...prev,
                        vacancyRatePercent: Number(e.target.value || 0),
                      }))
                    }
                  />
                </CommonFormControl>
              </Grid>
            )}
            {isFixAndFlip && (
              <>
                <Grid item xs={12} md={6}>
                  <CommonFormControl
                    label="Cost / Sqft:"
                    inLine
                    labelWidth={170}
                  >
                    <CommonNumberInput
                      name="constructionCostPerSqft"
                      size="small"
                      value={assumptions.constructionCostPerSqft}
                      onChange={(e) =>
                        setAssumptions((prev) => ({
                          ...prev,
                          constructionCostPerSqft: Number(e.target.value || 0),
                        }))
                      }
                    />
                  </CommonFormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CommonFormControl
                    label="Holding Period (months):"
                    inLine
                    labelWidth={170}
                  >
                    <CommonNumberInput
                      name="holdingPeriodMonths"
                      size="small"
                      value={assumptions.holdingPeriodMonths}
                      onChange={(e) =>
                        setAssumptions((prev) => ({
                          ...prev,
                          holdingPeriodMonths: Number(e.target.value || 0),
                        }))
                      }
                    />
                  </CommonFormControl>
                </Grid>
              </>
            )}
          </Grid>

          <Divider
            sx={{ borderColor: theme.palette.greyShades.border1, mb: 2 }}
          />

          <Typography
            variant="h6"
            fontWeight={700}
            gutterBottom
            sx={{ color: theme.palette.text.primary }}
          >
            Profitability Metrics
          </Typography>
          {isDscrOrCommercial && metrics && (
            <Box sx={{ mb: 2 }}>
              <CommonFormControl
                label="Effective Rent (monthly):"
                secondLabel={formatPrice(metrics.effectiveRent)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="NOI (monthly):"
                secondLabel={formatPrice(metrics.noi)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Cash Flow (monthly):"
                secondLabel={formatPrice(metrics.cashFlow)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="DSCR (vacancy-adjusted):"
                secondLabel={metrics.dscr ? metrics.dscr.toFixed(2) : 'N/A'}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Cap Rate:"
                secondLabel={formatPercent(metrics.capRate)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Cash-on-Cash:"
                secondLabel={formatPercent(metrics.cashOnCash)}
                inLine
                labelWidth={220}
              />
            </Box>
          )}

          {isFixAndFlip && metrics && (
            <Box sx={{ mb: 2 }}>
              <CommonFormControl
                label="Rehab Estimate:"
                secondLabel={formatPrice(metrics.rehabEstimate)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Total Project Cost:"
                secondLabel={formatPrice(metrics.totalProjectCost)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Estimated Profit:"
                secondLabel={formatPrice(metrics.profit)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Profit Margin:"
                secondLabel={formatPercent(metrics.profitMargin)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="ROI:"
                secondLabel={formatPercent(metrics.roi)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Annualized ROI:"
                secondLabel={formatPercent(metrics.annualizedRoi)}
                inLine
                labelWidth={220}
              />
            </Box>
          )}

          {isBridge && metrics && (
            <Box sx={{ mb: 2 }}>
              <CommonFormControl
                label="Interest-Only Payment:"
                secondLabel={formatPrice(metrics.monthlyInterestOnly)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Total Interest:"
                secondLabel={formatPrice(metrics.totalInterest)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Cash to Close:"
                secondLabel={formatPrice(metrics.cashToClose)}
                inLine
                labelWidth={220}
              />
              <CommonFormControl
                label="Total Cost:"
                secondLabel={formatPrice(metrics.totalCost)}
                inLine
                labelWidth={220}
              />
            </Box>
          )}

          <Divider
            sx={{ borderColor: theme.palette.greyShades.border1, mb: 2 }}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: theme.palette.text.primary }}
            >
              Market Comparison
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOrderAscending((prev) => !prev)}
              sx={{ color: theme.palette.text.secondary }}
              title={`Order ${orderAscending ? 'High to Low' : 'Low to High'}`}
            >
              <SwapVertIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box>
            {sortedMarketMetrics.map((metric) => (
              <CommonFormControl
                key={metric.id}
                label={`${metric.label}:`}
                secondLabel={metric.display}
                inLine
                labelWidth={220}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
